/**
 * Form validation utilities
 */

export interface ValidationError {
  field: string;
  message: string;
}

/**
 * Validate email format
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Validate password strength
 * Requires:
 * - At least 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 */
export function validatePassword(password: string): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  if (password.length < 8) {
    errors.push('Le mot de passe doit contenir au moins 8 caractères');
  }
  if (!/[A-Z]/.test(password)) {
    errors.push('Le mot de passe doit contenir une majuscule');
  }
  if (!/[a-z]/.test(password)) {
    errors.push('Le mot de passe doit contenir une minuscule');
  }
  if (!/[0-9]/.test(password)) {
    errors.push('Le mot de passe doit contenir un chiffre');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Validate login form
 */
export function validateLoginForm(email: string, password: string): ValidationError[] {
  const errors: ValidationError[] = [];

  if (!email.trim()) {
    errors.push({ field: 'email', message: 'L\'email est requis' });
  } else if (!validateEmail(email)) {
    errors.push({ field: 'email', message: 'Veuillez entrer un email valide' });
  }

  if (!password) {
    errors.push({ field: 'password', message: 'Le mot de passe est requis' });
  }

  return errors;
}

/**
 * Validate registration form
 */
export function validateRegisterForm(
  email: string,
  password: string,
  confirmPassword: string
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Email validation
  if (!email.trim()) {
    errors.push({ field: 'email', message: 'L\'email est requis' });
  } else if (!validateEmail(email)) {
    errors.push({ field: 'email', message: 'Veuillez entrer un email valide' });
  }

  // Password validation
  if (!password) {
    errors.push({ field: 'password', message: 'Le mot de passe est requis' });
  } else {
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      errors.push({ field: 'password', message: passwordValidation.errors[0] });
    }
  }

  // Confirm password validation
  if (!confirmPassword) {
    errors.push({ field: 'confirmPassword', message: 'Veuillez confirmer votre mot de passe' });
  } else if (password !== confirmPassword) {
    errors.push({ field: 'confirmPassword', message: 'Les mots de passe ne correspondent pas' });
  }

  return errors;
}
